// Data-rights server functions for the Settings → Data & Privacy panel.
// Implements the user rights promised in the Consumer Health Data Privacy Policy:
//   - Withdraw CHD consent
//   - Export ("download") my data
//   - Delete my account and all associated data
//
// Security:
//   - All three functions are scoped to context.userId from requireSupabaseAuth.
//   - Client-supplied user ids are never trusted; the server always uses the
//     auth-derived id.
//   - Row deletes are wrapped in an atomic SECURITY DEFINER Postgres function
//     (public.delete_user_account_data) called via supabaseAdmin.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { CHD_CONSENT_TYPE, CHD_POLICY_VERSION } from "@/lib/consent";

// ---------- 1) Withdraw CHD consent ----------
export const withdrawChdConsent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { error } = await (supabase as unknown as {
      from: (t: string) => { insert: (row: Record<string, unknown>) => Promise<{ error: { message: string } | null }> };
    }).from("consent_events").insert({
      user_id: userId,
      consent_type: CHD_CONSENT_TYPE,
      action: "withdrawn",
      policy_version: CHD_POLICY_VERSION,
      user_agent: null,
    });
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

// ---------- 2) Export ("download") my data ----------
// Returns the user's data as a JSON document plus 1-hour signed URLs for any
// uploaded files. Every query has an explicit user_id filter in addition to RLS.
const USER_TABLES = [
  "profiles", // primary key = user id, queried by id
  "consent_events",
  "daily_logs",
  "daily_moods",
  "daily_hrt",
  "daily_triggers",
  "symptom_events",
  "periods",
  "lab_uploads",
  "health_screenings",
  "wearable_connections",
  "wearable_metrics",
  "wearable_interest",
  "notification_preferences",
  "notification_sends",
  "push_subscriptions",
  "email_send_log",
  "referral_codes",
] as const;

export const exportMyData = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const data: Record<string, Array<Record<string, NonNullable<unknown>>>> = {};
    const sb = supabase as unknown as {
      from: (t: string) => {
        select: (cols: string) => {
          eq: (col: string, val: string) => Promise<{ data: Array<Record<string, NonNullable<unknown>>> | null; error: { message: string } | null }>;
          or: (filter: string) => Promise<{ data: Array<Record<string, NonNullable<unknown>>> | null; error: { message: string } | null }>;
        };
      };
    };

    for (const table of USER_TABLES) {
      const idCol = table === "profiles" ? "id" : "user_id";
      const { data: rows, error } = await sb.from(table).select("*").eq(idCol, userId);
      if (error) throw new Error(`${table}: ${error.message}`);
      data[table] = rows ?? [];
    }


    // referral_events: user may appear as referrer OR referred
    const { data: refEvents, error: refErr } = await supabase
      .from("referral_events")
      .select("*")
      .or(`referrer_user_id.eq.${userId},referred_user_id.eq.${userId}`);
    if (refErr) throw new Error(`referral_events: ${refErr.message}`);
    data["referral_events"] = (refEvents ?? []) as Array<Record<string, NonNullable<unknown>>>;

    // Signed URLs for uploaded files (1-hour expiry).
    const fileLinks: Array<{ bucket: string; path: string; signedUrl: string }> = [];
    for (const bucket of ["lab-uploads", "doctor-reports"] as const) {
      const { data: list } = await supabase.storage
        .from(bucket)
        .list(userId, { limit: 1000 });
      if (!list) continue;
      for (const f of list) {
        const path = `${userId}/${f.name}`;
        const { data: signed } = await supabase.storage
          .from(bucket)
          .createSignedUrl(path, 60 * 60);
        if (signed?.signedUrl) {
          fileLinks.push({ bucket, path, signedUrl: signed.signedUrl });
        }
      }
    }

    return {
      ok: true as const,
      exportedAt: new Date().toISOString(),
      userId,
      data,
      files: fileLinks,
      filesNote: "Signed file links expire in 1 hour.",
    };
  });

// ---------- 3) Delete my account and all associated data ----------
// Order:
//   1. Cancel active Stripe subscription (best-effort; failure does NOT block delete).
//   2. Delete all relational rows atomically via SECURITY DEFINER function.
//   3. Best-effort storage cleanup.
//   4. Delete auth user (irrevocable). Must be last.
export const deleteMyAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // 1) Best-effort Stripe cancellation. Uses the user's own profile row
    //    (RLS-protected) to read the subscription id — no cross-user access.
    let stripeCancelled: boolean | null = null;
    let stripeError: string | null = null;
    try {
      const { data: prof } = await supabase
        .from("profiles")
        .select("subscription_id, subscription_status")
        .eq("id", userId)
        .maybeSingle();
      const subId = prof?.subscription_id;
      const status = prof?.subscription_status;
      if (subId && status && ["active", "trialing", "past_due"].includes(status)) {
        const { stripe } = await import("@/server/stripe/stripe.server");
        await stripe().subscriptions.cancel(subId);
        stripeCancelled = true;
      } else {
        stripeCancelled = null; // nothing to cancel
      }
    } catch (e) {
      stripeCancelled = false;
      stripeError = e instanceof Error ? e.message : String(e);
      console.error("[deleteMyAccount] stripe cancel failed", {
        userId,
        error: stripeError,
      });
    }

    // 2) Atomic relational delete (all-or-nothing).
    const { error: dbErr } = await supabaseAdmin.rpc(
      "delete_user_account_data" as never,
      { _user_id: userId } as never,
    );
    if (dbErr) {
      return {
        ok: false as const,
        step: "db" as const,
        error: dbErr.message,
        stripeCancelled,
        stripeError,
      };
    }

    // 3) Storage cleanup. If this fails we ABORT before deleting the auth user,
    //    so the user can retry: the RPC above is idempotent (rows already gone),
    //    storage cleanup will retry, then the auth user is deleted last.
    //    Deleting the auth user here would orphan health-data files with no
    //    way to clean them up.
    try {
      for (const bucket of ["lab-uploads", "doctor-reports"] as const) {
        const { data: list, error: listErr } = await supabaseAdmin.storage
          .from(bucket)
          .list(userId, { limit: 1000 });
        if (listErr) throw listErr;
        const paths = (list ?? []).map((f) => `${userId}/${f.name}`);
        if (paths.length > 0) {
          const { error: rmErr } = await supabaseAdmin.storage
            .from(bucket)
            .remove(paths);
          if (rmErr) throw rmErr;
        }
      }
    } catch (e) {
      const storageError = e instanceof Error ? e.message : String(e);
      console.error("[deleteMyAccount] storage cleanup failed; aborting before auth delete", {
        userId,
        error: storageError,
      });
      return {
        ok: false as const,
        step: "storage" as const,
        error: storageError,
        stripeCancelled,
        stripeError,
      };
    }

    // 4) Delete the auth user (irrevocable). Must be last.
    const { error: authErr } = await supabaseAdmin.auth.admin.deleteUser(userId);
    if (authErr) {
      return {
        ok: false as const,
        step: "auth" as const,
        error: authErr.message,
        stripeCancelled,
        stripeError,
      };
    }

    return {
      ok: true as const,
      stripeCancelled,
      stripeError,
    };
  });

